<template>
  <PageWrap
    :page-title="pageTitle"
    class="charge-detail-wrap"
    :class="{ '-warning-notice-visible': displayWarningNotice }"
  >
    <LoadingIndicator v-if="loadingData" class="mt-5" />
    <template v-else>
      <!-- FORM SECTION: Intro -->
      <b-row class="mb-3">
        <b-col cols="1">
          <BfInput
            label="Estado"
            data-key="estado"
            :options="estado"
            :value="formData.estado_texto"
            :edit-mode="isEditing"
            :errors="formErrors"
            readonly
            @update="updateEstado"
          />
        </b-col>
        <b-col cols="2">
          <BfDatePicker
            label="Fecha alta"
            data-key="key"
            readonly
            :value="formData.created_at"
            :edit-mode-value="formatearFecha(formData.created_at)"
            type="date"
          />
        </b-col>
        <b-col cols="2">
          <BfDatePicker
            label="Fecha recepción *"
            data-key="fecha_hora_recepcion"
            :edit-mode="isEditing"
            :errors="formErrors"
            :value="formData.fecha_hora_recepcion"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="dd/MM/yyyy HH:mm"
            type="datetime"
            @update="updateForm"
            :edit-mode-value="formatearFecha(formData.fecha_hora_recepcion)"
          />
        </b-col>
        <b-col cols="2">
          <BfDatePicker
            label="Fecha entrega *"
            data-key="fecha_hora_limite"
            :edit-mode="isEditing"
            :errors="formErrors"
            :value="formData.fecha_hora_limite"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="dd/MM/yyyy HH:mm"
            type="datetime"
            :edit-mode-value="formatearFecha(formData.fecha_hora_limite)"
            @update="updateForm"
          />
        </b-col>
        <b-col cols="2">
          <BfDatePicker
            label="Fecha envío"
            data-key="fecha_hora_envio"
            :edit-mode="isEditing"
            @update="updateForm"
            :errors="formErrors"
            readonly
            :value="formData.fecha_hora_envio"
            value-format="yyyy-MM-dd HH:mm:ss"
            :edit-mode-value="formatearFecha(formData.fecha_hora_envio)"
          />
        </b-col>
        <b-col cols="1" class="align-items-center">
          <div>
            <BfCheckbox
              label="Urgente"
              :display-label="false"
              data-key="urgente"
              :value="formData.urgente"
              :errors="formErrors"
              :edit-mode="isEditing"
              :class="{ 'pr-2': isAdmin, mb0: true }"
              @update="updateForm"
            />
          </div>

          <div>
            <BfCheckbox
              v-if="puedeSerAnulado && isEditing"
              label="Anulado"
              :display-label="false"
              data-key="anulado"
              :value="formData.anulado"
              :errors="formErrors"
              :edit-mode="isEditing"
              @update="updateForm"
              class="mb0"
            />
          </div>
          <div>
            <BfCheckbox
              v-if="puedeSerAnulado && isEditing"
              :display-label="false"
              label="Paralizado"
              data-key="paralizado"
              :value="formData.paralizado"
              :errors="formErrors"
              :edit-mode="isEditing"
              @update="updateForm"
              class="mb0"
            />
          </div>
        </b-col>
        <b-col cols="1" class="align-items-center">
          <BfButton
            v-if="isAdmin"
            class="ml-auto"
            @click="headerWarningButtonClick"
          >
            Avisos
          </BfButton>
          <BfButton
            v-if="isAdmin"
            class="ml-auto mt-2"
            variant="secondary"
            @click="displayModalCreateCodigoPostal"
          >
            Nuevo C.P
          </BfButton>
        </b-col>
      </b-row>

      <b-card no-body>
        <b-tabs card v-model="activeTab">
          <b-tab title="Información">
            <b-row>
              <b-col cols="12" md="6" class="pr-md-4 pr-xl-5">
                <!-- FORM SECTION: General -->
                <b-row class="mb-3">
                  <b-col cols="12">
                    <FormSectionHeader title="General">
                      <template slot="side-content">
                        <BfCheckbox
                          v-if="isEditing"
                          label="Tenemos documentación"
                          data-key="tiene_documentacion"
                          :value="formData.tiene_documentacion"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          :bottom-gutter="false"
                          :display-label="false"
                          @update="updateForm"
                        />
                        <BfSelect
                          v-if="!isEditing"
                          label="Tenemos documentación *"
                          data-key="tiene_documentacion"
                          :edit-mode="isEditing"
                          :options="tenemosDocumentacionOptions"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </template>
                    </FormSectionHeader>
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Nº de Encargo *"
                      data-key="numero_encargo"
                      :value="formData.numero_encargo_formateado"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>

                  <b-col cols="4">
                    <BfSelect
                      label="Cliente *"
                      data-key="empresasclientes_id"
                      :edit-mode="isEditing"
                      :options="empresaClienteSelectOptions"
                      :value="formData.empresa_cliente_nombre"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>

                  <b-col cols="4">
                    <BfSelect
                      label="Empresa *"
                      data-key="delegaciones_id"
                      :options="clientes"
                      :value="formData.delegacion_nombre"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>

                  <b-col cols="4">
                    <BfInput
                      label="Lote"
                      data-key="lote"
                      :edit-mode="isEditing"
                      @update="updateForm"
                      :value="formData.lote"
                      :errors="formErrors"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Expediente"
                      data-key="expediente"
                      :value="formData.expediente"
                      :edit-mode="isEditing"
                      @update="updateForm"
                      :errors="formErrors"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfSelect
                      label="Tasador Oficial *"
                      data-key="tasador_oficial_id"
                      :options="tasadoresSelect"
                      :value="selectedTasadorOficial"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateTasadorOficial"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Nº de Valoración (Valtin-)"
                      data-key="num_valoracion"
                      :value="formData.num_valoracion"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Nº Préstamo"
                      data-key="num_prestamo"
                      :value="formData.num_prestamo"
                      :edit-mode="isEditing"
                      @update="updateForm"
                      :errors="formErrors"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Número interno"
                      data-key="num_interno_sucursal"
                      :value="formData.num_interno_sucursal"
                      :edit-mode="isEditing"
                      @update="updateForm"
                      :errors="formErrors"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfSelect
                      label="Comercial"
                      data-key="comercial"
                      :options="comercialesSelect"
                      :value="formData.comercial_nombre"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateComercial"
                    />
                  </b-col>

                  <b-col cols="4">
                    <!-- <BfInput
                      label="Comercial"
                      data-key="comercial"
                      :edit-mode="isEditing"
                      @update="updateForm"
                      :errors="formErrors"
                      :value="formData.comercial"

                    />-->
                  </b-col>
                </b-row>

                <!-- FORM SECTION: Sucursal -->
                <b-row class="mb-3">
                  <b-col cols="12">
                    <FormSectionHeader
                      title="Sucursal"
                      display-buttons
                      :disable-edit-button="!hasSelectedBranch"
                      :disable-remove-button="!hasSelectedBranch"
                      @button-click="branchHeaderButtonClick"
                    />
                  </b-col>

                  <b-col cols="12">
                    <BranchForm
                      :data="branchFormData"
                      :editing="isEditing"
                      allow-search
                      readonly
                      @select-search-item="selectBranch"
                    />
                  </b-col>
                  <!--Sucursal asociada -->
                  <b-col cols="12">
                    <BfInput
                      label="Sucursal asociada al encargo"
                      data-key="sucursal_asociada"
                      :edit-mode="isEditing"
                      @update="updateForm"
                      :errors="formErrors"
                      :value="formData.sucursal_asociada"
                      type="textarea"
                      :autosize="{ minRows: 5, maxRows: 10 }"
                    />
                  </b-col>
                </b-row>

                <!-- FORM SECTION: Solicitante -->
                <b-row class="mb-3">
                  <b-col cols="12">
                    <FormSectionHeader
                      title="Solicitante *"
                      display-buttons
                      :disable-edit-button="!hasSelectedApplicant"
                      :disable-remove-button="!hasSelectedApplicant"
                      @button-click="applicantHeaderButtonClick"
                    />
                  </b-col>
                  <b-col cols="12">
                    <div v-if="missingApplicantError">
                      <p
                        v-for="error in missingApplicantError"
                        :key="error"
                        class="text-danger font-weight-bold"
                      >
                        {{ error }}
                      </p>
                    </div>

                    <SolicitanteEnEncargoForm
                      :data="applicantFormData"
                      :editing="isEditing"
                      allow-search
                      readonly
                      @select-search-item="selectApplicant"
                    />
                  </b-col>
                </b-row>

                <!-- Contacto del encargo -->

                <b-row class="mb-3">
                  <b-col cols="12">
                    <FormSectionHeader title="Contactos" />
                  </b-col>
                  <b-col cols="8">
                    <BfInput
                      label="Contacto"
                      data-key="contacto_persona1"
                      :value="formData.contacto_persona1"
                      :edit-mode="isEditing"
                      @update="updateForm"
                      :errors="formErrors"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Teléfono"
                      data-key="contacto_telefono1"
                      :value="formData.contacto_telefono1"
                      :errores="formErrors"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="8">
                    <BfInput
                      label="Contacto 2"
                      data-key="contacto_persona2"
                      :value="formData.contacto_persona2"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Teléfono"
                      data-key="contacto_telefono2"
                      :value="formData.contacto_telefono2"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                </b-row>
              </b-col>

              <!--end first column-->

              <b-col cols="12" md="6" class="pl-md-4 pl-xl-5">
                <!-- FORM SECTION: Inmueble -->
                <b-row class="mb-3">
                  <b-col cols="12">
                    <FormSectionHeader title="Inmueble">
                      <template slot="side-content">
                        <BfCheckbox
                          v-if="isEditing"
                          label="Tiene antecedente"
                          data-key="inmueble_antecedente"
                          :value="formData.inmueble_antecedente"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          :bottom-gutter="false"
                          :display-label="false"
                          @update="updateForm"
                        />
                        <BfSelect
                          v-if="!isEditing"
                          label="Tiene antecedente *"
                          data-key="inmueble_antecedente"
                          :edit-mode="isEditing"
                          :options="tenemosDocumentacionOptions"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                        <BfCheckbox
                          v-if="isEditing"
                          label="Visita Interior"
                          data-key="inmueble_visita_exterior"
                          :value="formData.inmueble_visita_exterior"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          :bottom-gutter="false"
                          :display-label="false"
                          @update="updateForm"
                        />
                        <BfSelect
                          v-if="!isEditing"
                          label="Visita Interior *"
                          data-key="inmueble_visita_exterior"
                          :edit-mode="isEditing"
                          :options="tenemosDocumentacionOptions"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </template>
                    </FormSectionHeader>
                  </b-col>
                  <b-col cols="4">
                    <BfSelect
                      label="Tipo inmueble"
                      data-key="tiposinmuebles_id"
                      :options="propiertiesSelect"
                      :edit-mode="isEditing"
                      :value="formData.tipo_inmueble_nombre"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfSelect
                      label="Tipo trabajo"
                      data-key="tipostrabajos_id"
                      :edit-mode="isEditing"
                      :options="jobsSelect"
                      :value="formData.tipo_trabajo_nombre"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfSelect
                      label="Finalidad"
                      data-key="inmueble_finalidad"
                      :value="formData.inmueble_finalidad"
                      :edit-mode="isEditing"
                      :options="finalidadesInmuebleSelect"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <!-- <b-col cols="4">
                    <BfSelect
                      label="Estado"
                      data-key="inmueble_estado"
                      :options="estadosInmueblesSelect"
                      :value="formData.inmueble_estado"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>-->
                  <b-col cols="4">
                    <BfInput
                      label="Dirección *"
                      data-key="inmueble_direccion"
                      :value="formData.inmueble_direccion"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Num Antecedente"
                      data-key="inmueble_numantecedente"
                      :value="formData.inmueble_numantecedente"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfAutocomplete
                      label="Código Postal"
                      data-key="inmueble_codigopostal"
                      :value="formData.inmueble_codigopostal"
                      :fetch-suggestions="autocompleteSearchCodigosPostales"
                      :trigger-on-focus="true"
                      @input="seleccionadoUnCp"
                    />
                   <!--  <BfInput
                      v-if="!isEditing"
                      label="Código Postal *"
                      data-key="inmueble_codigopostal"
                      :value="formData.inmueble_codigopostal"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateCodigoPostal"
                    />
                    <BfInput
                      v-else
                      label="Código Postal *"
                      data-key="inmueble_codigopostal"
                      :value="formData.inmueble_codigopostal"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateCodigoPostal"
                    /> -->
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Municipio"
                      data-key="inmueble_municipio"
                      :value="formData.inmueble_municipio"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                      readonly
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Población"
                      data-key="inmueble_poblacion"
                      :value="formData.inmueble_poblacion"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                      readonly
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Provincia"
                      data-key="inmueble_provincia"
                      :value="formData.inmueble_provincia"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                      readonly
                    />
                  </b-col>
                </b-row>

                <!-- FORM SECTION: Datos registrales -->
                <b-row class="mb-3">
                  <b-col cols="12">
                    <FormSectionHeader title="Datos registrales" small />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Idufir"
                      data-key="registro_idufir"
                      :value="formData.registro_idufir"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="4">
                    <BfInput
                      label="Ref. Catastral"
                      data-key="registro_refcatastral"
                      :value="formData.registro_refcatastral"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="3">
                    <BfInput
                      label="Registro"
                      data-key="registro_nombre"
                      :value="formData.registro_nombre"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                    />
                  </b-col>
                  <b-col cols="9">
                    <b-row>
                      <b-col>
                        <BfInput
                          label="Sección"
                          data-key="registro_seccion"
                          :value="formData.registro_seccion"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                      <b-col>
                        <BfInput
                          label="Finca"
                          data-key="registro_finca"
                          :value="formData.registro_finca"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                      <b-col>
                        <BfInput
                          label="Tomo"
                          data-key="registro_tomo"
                          :value="formData.registro_tomo"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                      <b-col>
                        <BfInput
                          label="Libro"
                          data-key="registro_libro"
                          :value="formData.registro_libro"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                      <b-col>
                        <BfInput
                          label="Folio"
                          data-key="registro_folio"
                          :value="formData.registro_folio"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>

                <!-- Observaciones -->
                <b-row class="mb-3">
                  <b-col cols="12">
                    <FormSectionHeader title="Observaciones" small />
                  </b-col>
                  <b-col cols="12">
                    <BfInput
                      label="Observaciones"
                      data-key="observaciones"
                      :value="formData.observaciones"
                      :edit-mode="isEditing"
                      :errors="formErrors"
                      @update="updateForm"
                      type="textarea"
                    />
                  </b-col>
                </b-row>

                <!-- FORM SECTION: Valoración -->
                <b-row class="mb-3" v-if="!isTasador">
                  <b-col cols="12">
                    <FormSectionHeader title="Valoración" />

                    <b-row>
                      <b-col xl="6">
                        <BfInput
                          label="Valoración tasación"
                          data-key="valoracion_tasacion"
                          :value="formData.valoracion_tasacion"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col xl="6">
                        <BfInput
                          label="Honorarios tasación"
                          data-key="honorarios_tasacion"
                          :value="formData.honorarios_tasacion"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col xl="6">
                        <BfInput
                          label="Gastos desplazamiento"
                          data-key="gastos_desplazamiento"
                          :value="formData.gastos_desplazamiento"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col xl="6">
                        <BfInput
                          label="Facturación neta"
                          data-key="facturacion_neta"
                          :value="formData.facturacion_neta"
                          :edit-mode="isEditing"
                          :errors="formErrors"
                          @update="updateForm"
                        />
                      </b-col>
                    </b-row>

                    <div v-if="!isEditing" class="mt-2">
                      <BfButton @click="saveChargeClick" class="mr-3 mb-3">
                        Guardar
                      </BfButton>
                      <BfButton
                        @click="saveChargeAndMoveClick"
                        class="mr-3 mb-3"
                      >
                        Guardar y mover a asignación
                      </BfButton>
                      <BfButton
                        @click="saveChargeAndCreateAnotherClick"
                        class="mb-3"
                      >
                        Guardar y añadir otro
                      </BfButton>
                    </div>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-tab>
          <b-tab title="Documentación" :disabled="!isEditing">
            <b-row class="mb-4">
              <b-col cols="12" lg="7">
                <BfInput
                  v-model="documentationSearch"
                  data-key="documentationSearch"
                  label="Búsqueda"
                  placeholder="Filtrar por nombre"
                  @input="updateDocumentationSearch"
                />
              </b-col>
              <b-col cols="12" lg="5" class="d-flex flex-wrap">
                <BfCheckbox
                  v-for="{ label, id, active } in documentationFilterCheckboxes"
                  :key="id"
                  :data-key="`doc_filter_${label}`"
                  :label="label"
                  :value="active"
                  :bottom-gutter="false"
                  class="documentation-filter-checkbox mr-3"
                  @input="changeDocumentationFilterCheckbox(id)"
                />
              </b-col>
            </b-row>

            <FileDropzone
              id="documentation_dropzone"
              ref="documentDropzone"
              :options="dropOptions"
              @files-added="documentationFilesAdded"
            >
              <BfTable
                :data="documentationTableData"
                :columns="documentationTableColumns"
                :header-cell-class-name="documentationTableHeaderCellClassName"
                :cell-class-name="documentationTableCellClassName"
                class="documentation-table"
                @row-click="documentationTableRowClick"
                show-checkbox-column
                :checked-rows="checkedDocumentationRows"
                @row-checkbox-change="documentsCheckboxChange"
                :header-checkbox-checked="allTableRowsSelected"
                @header-checkbox-change="headerCheckboxChange"
              >
                <span
                  class="tasaciones-table-marker"
                  slot="titulo"
                  slot-scope="{ row }"
                >
                  {{ row.titulo }}
                </span>
                <span
                  class="el-icon-view documentation-table-icon"
                  slot="open"
                />
                <span
                  class="el-icon-delete documentation-table-icon text-danger"
                  slot="delete"
                />
              </BfTable>
            </FileDropzone>

            <b-row class="mb-4">
              <b-col cols="12" lg="7">
                <bfButton
                  class="mt-4"
                  v-if="changeDocumentationTitleButton"
                  @click="openDocumentationChangeNameModal"
                >
                  Editar nombre
                </bfButton>
                <bfButton
                  class="mt-4 ml-1"
                  v-if="changeDocumentationTitleButton"
                  @click="openCategoriaChangeModal"
                >
                  Editar categoría
                </bfButton>
                <bfButton
                  class="mt-4 ml-1"
                  variant="red"
                  v-if="checkedDocumentationRows.length > 1"
                  @click="openDeleteDocumentation"
                >
                  Eliminar documentos seleccionados
                </bfButton>
                <bfButton
                  class="mt-4 ml-1"
                  variant="secondary"
                  v-if="checkedDocumentationRows.length > 1"
                  @click="starDownloadMultiplesFilesAsZip"
                >
                  Descargar documentos seleccionados
                </bfButton>
              </b-col>
            </b-row>

            <div v-if="documentationTableData.length" class="mt-4 text-right">
              <BfPagination
                :current-page="documentationCurrentPage"
                :total="documentationTotalRows"
                :page-size="documentationPerPage"
                @current-change="updateDocumentationPagination"
              />
            </div>
          </b-tab>
        </b-tabs>
      </b-card>

      <div v-if="displayDeleteChargeButton" class="mt-4">
        <BfButton variant="red" @click="deleteChargeButtonClick">
          Eliminar encargo
        </BfButton>
      </div>

      <!-- Form Modals -->
      <CreateBranchModal
        v-model="displayCreateBranchModal"
        :form-errors="createBranchErrors"
        @update="createBranchModal"
      />

      <CreateApplicantModal
        v-model="displayCreateApplicantModal"
        :form-errors="createApplicantErrors"
        @save-data="createApplicant"
      />

      <EditApplicantModal
        v-model="displayEditApplicantModal"
        :form-data="applicantFormData"
        :form-errors="editApplicantErrors"
        @update="editApplicantData"
      />

      <EditBranchModal
        v-model="displayEditBranchModal"
        :form-data="branchFormData"
        :form-errors="editBranchErrors"
        @update="editBranchData"
      />

      <FileFormModal
        v-model="displayDocumentationFormModal"
        :categories="documentationCategories"
        :multiple-files="multipleUploadFiles"
        :initial-form-data="documentationFormData"
        :is-editing="editingExistingDocument"
        :is-uploading="documentationIsUploading"
        :form-errors="documentationFormErrors"
        :upload-size-progress="documentationSizeProgress"
        :upload-count-progress="documentationCountProgress"
        @save-data="saveDocumentationFileForm"
      />

      <ChargeWarningModal
        v-model="displayWarningsFormModal"
        :form-errors="formErrors"
        :warning-value="formData.advertencias"
        @save-data="updateChargeWarningModal"
      />

      <DeleteWarningModal
        v-model="displayRemoveBranchModal"
        title="¿Desea que el encargo no tenga sucursal?"
        @confirm-deletion="removeBranch"
      />

      <DeleteWarningModal
        v-model="displayRemoveApplicantModal"
        title="¿Desea que el encargo no tenga solicitante?"
        @confirm-deletion="removeApplicant"
      />

      <DeleteWarningModal
        v-model="displayDeleteDocumentationModal"
        title="¿Desea borrar este documento?"
        @confirm-deletion="deleteDocumentation"
      />

      <div
        v-if="displayWarningNotice"
        class="charge-warning-overlay bg-warning"
      >
        <header class="warning-overlay-header">
          <i class="header-icon fas fa-exclamation-triangle"></i>
          <h4 class="header-label">Mensaje de Aviso</h4>

          <button class="header-close-button" @click="closeWarningNotice">
            <i class="fas fa-times"></i>
          </button>
        </header>

        <p class="warning-body">{{ formData.advertencias }}</p>
      </div>

      <DeleteWarningModal
        v-model="displayDeleteChargeModal"
        title="¿Desea borrar este encargo?"
        @confirm-deletion="deleteCharge"
      />
      <CreateCodigoPostalModal
        v-model="displayCreateCodigoPostalModal"
        title="Nuevo código postal"
      />

      <!-- Modales de la Documentación -->
      <DeleteWarningModal
        v-model="displayDeleteMultipeFilesModal"
        title="¿Desea borrar los documentos seleccionados?"
        @confirm-deletion="deleteDocumentsMultiple"
      />
      <documentacionChangeNameModal
        v-model="displayDocumentationChangeNameModal"
        :initialFormData="this.checkedDocumentationRows"
        @updateDocuments="this.loadDocuments"
      ></documentacionChangeNameModal>
      <documentacionChangeCategoryModal
        v-model="displayDocumentationChangeCategoryModal"
        :categorias="this.documentationCategories"
        :mainCategory="this.mainCategoryDocumentsSelected"
        :initialFormData="this.checkedDocumentationRows"
        @updateDocuments="this.loadDocuments"
      ></documentacionChangeCategoryModal>
    </template>
  </PageWrap>
</template>

<script>
/* eslint-disable object-curly-newline */
import { mapState, mapGetters } from 'vuex'
import ApplicantForm from '@/components/FormGroups/ApplicantForm'
import SolicitanteEnEncargoForm from '@/components/FormGroups/SolicitanteEnEncargoForm'
import BranchForm from '@/components/FormGroups/BranchForm'
import ChargeWarningModal from '@/components/FormModals/ChargeWarningModal'
import CreateCodigoPostalModal from '@/components/FormModals/CreateCodigoPostalModal'

import CreateApplicantModal from '@/components/FormModals/CreateApplicantModal'
import CreateBranchModal from '@/components/FormModals/CreateBranchModal'
import EditApplicantModal from '@/components/FormModals/EditApplicantModal'
import EditBranchModal from '@/components/FormModals/EditBranchModal'
import FileDropzone from '@/components/FileDropzone'
import FileFormModal from '@/components/FormModals/FileFormModal'
import documentacionChangeNameModal from '@/components/FormModals/documentacionChangeNameModal'
import documentacionChangeCategoryModal from '@/components/FormModals/documentacionChangeCategoryModal'

import DeleteWarningModal from '@/components/DeleteWarningModal'
import FormSectionHeader from '@/components/FormSectionHeader'
import LoadingIndicator from '@/components/LoadingIndicator'
import PageWrap from '@/components/PageWrap'
import {
  branchAPI,
  chargeAPI,
  clientAPI,
  documentAPI,
  codigosPostalesApi,
} from '@/api'
import { backendUrlBase } from '@/api/utils'
import { debounce, get, isEmpty } from '@/utils'
import { uploadChargeDocuments } from '@/utils/abstract'

export default {
  name: 'ChargesDetail',

  props: {
    id: {
      type: String,
    },
  },

  data() {
    return {
      activeTab: this.$route.query.activeTab || 0,
      formData: {},
      formErrors: {},
      clientes: [],
      tasadoresSelect: [],
      comercialesSelect: [],
      tasadoresRaw: [],
      comercialesRaw: [],
      estadosInmueblesSelect: [],
      checkedDocumentationRows: [],
      estado: [
        { value: 'A', label: 'Estado A' },
        { value: 'B', label: 'Estado B' },
        { value: 'C', label: 'Estado C' },
        { value: 'D', label: 'Estado D' },
        { value: 'E', label: 'Estado E' },
      ],
      displayCreateApplicantModal: false,
      displayCreateBranchModal: false,
      displayEditApplicantModal: false,
      displayEditBranchModal: false,
      displayRemoveApplicantModal: false,
      displayRemoveBranchModal: false,
      displayDeleteMultipeFilesModal: false,
      displayCreateCodigoPostalModal: false,
      loadingData: false,
      delegacionesId: '',
      jobs: {},
      jobsSelect: [],
      properties: {},
      propiertiesSelect: [],
      finalidadesInmuebleSelect: [],
      empresaClienteSelectOptions: [],
      tenemosDocumentacionOptions: [
        {
          label: 'SI',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
      createBranchErrors: {},
      createApplicantErrors: {},
      editBranchErrors: {},
      editApplicantErrors: {},

      documentationData: [],
      documentationPerPage: 50,
      documentationCurrentPage: 1,
      documentationTotalRows: 0,
      documentationCategories: [],
      documentationSearch: null,
      documentationActiveFilters: [],
      displayDocumentationFormModal: false,
      displayDeleteDocumentationModal: false,
      selectedDocumentationTableRow: {},
      documentationUploadFiles: [],
      documentationFormErrors: {},
      documentationIsUploading: false,
      documentationCountProgress: {},
      documentationSizeProgress: {},
      editingExistingDocument: false,
      dropOptions: {
        url: 'https://holamundo.me/',
        autoQueue: false,
        autoProcessQueue: false,
        createImageThumbnails: false,
        clickable: false,
      },

      displayWarningsFormModal: false,
      displayWarningNotice: false,
      displayDeleteChargeModal: false,
      // Modales  para Gestor de documentos
      displayDocumentationChangeNameModal: false,
      displayDocumentationChangeCategoryModal: false,
      mainCategoryDocumentsSelected: '',
      autoCodigoPostalOptions: undefined,
    }
  },

  async created() {
    this.loadingData = true

    try {
      await this.prepareChargeForm()

      await Promise.all([
        ...(this.isEditing
          ? [this.loadCharge(), this.loadDocuments()]
          : [
              (this.formData.tiene_documentacion = null),
              (this.formData.inmueble_antecedente = null),
              (this.formData.inmueble_visita_exterior = null),
            ]), // Establecemos estos datos en null para que funcione el la spec cod 213
        this.loadDocumentCategories(),
      ])
    } finally {
      this.loadingData = false

      // Show "advertencia" modal onLoad if value exists
      if (
        this.isEditing &&
        !isEmpty(this.formData) &&
        this.formData.advertencias !== null
      ) {
        this.displayWarningNotice = true
      }
    }
  },

  methods: {
    // Este método modifica los CP tras marcar un codigo postal en el "autocomplete"
    async seleccionadoUnCp(value) {
      const part = value.substring(
        value.lastIndexOf(':') + 1,
        value.lastIndexOf(';'),
      )

      const data = await codigosPostalesApi.get(part)

      if (!this.isEditing) {
        this.formData.inmueble_poblacion = data.data.poblacion
        this.formData.inmueble_provincia = data.data.provincia
        this.formData.inmueble_municipio = data.data.municipio
        this.formData.inmueble_codigopostal = data.data.codigopostal
      } else {
        await this.updateForm({
          key: 'inmueble_poblacion',
          value: data.data.poblacion,
        })
        await this.updateForm({
          key: 'inmueble_provincia',
          value: data.data.provincia,
        })
        await this.updateForm({
          key: 'inmueble_municipio',
          value: data.data.municipio,
        })
        await this.updateForm({
          key: 'inmueble_codigopostal',
          value: data.data.codigopostal,
        })
        console.log('Loading data')

        await this.loadCharge();

                console.log('loaded data')
      }
    },
    async autocompleteSearchCodigosPostales(query, cb) {
      // console.log('Query: ' + query)
      if (query.length >= 2) {
        const dataSelect = await chargeAPI.autoCodigosVersion2(query)

        this.autoCodigoPostalOptions = dataSelect.data

        const formatted = dataSelect.data.map((cp) => ({
          key: cp.id,
          value: `${cp.poblacion}, ${cp.municipio}, ${cp.provincia} (:${cp.id};)`,
        }))

        cb(formatted)
      }
    },

    displayModalCreateCodigoPostal() {
      this.displayCreateCodigoPostalModal = true
    },
    async starDownloadMultiplesFilesAsZip() {
      const documentsSelected = this.checkedDocumentationRows
      const encargo_id = this.id

      const data = {
        documentos: documentsSelected,
        encargo: encargo_id,
      }

      try {
        const { url, message } = await documentAPI.starMultipleDownloadAsZip(
          data,
        )
        window.open(`${backendUrlBase}${url}`, 'download')
        this.$bfNotify({
          type: 'success',
          title: 'Fichero generado con éxito',
          message,
        })
        this.checkedDocumentationRows = []
      } catch ({ errors, status }) {
        this.$bfNotify({
          type: 'error',
          title: 'Errores al generar',
          message,
        })
      }
    },
    openDeleteDocumentation() {
      this.displayDeleteMultipeFilesModal = true
    },
    async deleteDocumentsMultiple() {
      const documentsSelected = this.checkedDocumentationRows
      await documentsSelected.forEach((element) => {
        documentAPI.delete(element)
      })
      await this.loadDocuments()

      this.$bfNotify({
        type: 'success',
        title: 'Documentos eliminados con éxito',
        message: 'Documentos eliminados con éxito.',
      })

      this.displayDeleteMultipeFilesModal = false
      this.selectedDocumentationTableRow = {}
    },
    formatearFecha(fecha) {
      const d = new Date(fecha)
      return d.toLocaleDateString('es-ES')
    },
    headerCheckboxChange(value) {
      if (value) {
        this.checkedDocumentationRows = this.documentationTableData.map(
          (c) => c.id,
        )
      } else {
        this.checkedDocumentationRows = []
      }
    },
    async loadMainCategory() {
      const category = await documentAPI.checkMainCategory({
        documentsId: this.checkedDocumentationRows,
      })

      this.mainCategoryDocumentsSelected = category
    },
    async openCategoriaChangeModal() {
      await this.loadMainCategory()
      this.displayDocumentationChangeCategoryModal = true
    },
    closeCategoriaChangeModal() {
      this.displayDocumentationChangeCategoryModal = true
    },
    openDocumentationChangeNameModal() {
      // alert(this.checkedDocumentationRows.length);
      this.displayDocumentationChangeNameModal = true
    },
    closeDocumentationChangeNameModal() {
      this.displayDocumentationChangeNameModal = false
    },
    async loadCharge() {
      try {
        const {
          data: [chargeData],
        } = await chargeAPI.get(this.id)

        this.formData = chargeData
      } catch ({ errors, status }) {
        if (errors) {
          this.$bfNotify({
            type: 'error',
            title: 'Error',
            message: errors,
          })
        }

        if (status === 404) {
          this.$router.replace({ name: 'charges' })
        }
      }
    },

    async loadDocuments() {
      const {
        data: {
          current_page: currentPage = 0,
          data = [],
          per_page: perPage = 0,
          total,
        },
      } = await documentAPI.getAllForCharge({
        chargeId: this.id,
        title: this.documentationSearch,
        category: this.documentationActiveFilters.join(','),
        page: this.documentationCurrentPage,
        perPage: this.documentationPerPage,
      })

      this.documentationData = data
      this.documentationPerPage = Number(perPage)
      this.documentationCurrentPage = currentPage
      this.documentationTotalRows = total
    },

    documentsCheckboxChange({ value }) {
      const currentIndex = this.checkedDocumentationRows.indexOf(value)

      if (currentIndex >= 0) {
        this.$delete(this.checkedDocumentationRows, currentIndex)
      } else {
        this.checkedDocumentationRows = [
          ...this.checkedDocumentationRows,
          value,
        ]
      }
    },

    async loadDocumentCategories() {
      const { categorias = [] } = await documentAPI.preCreate()
      this.documentationCategories = categorias
    },

    async prepareChargeForm() {
      const { data = {}, related = {} } = await chargeAPI.preCreate()

      if (!this.isEditing) {
        this.formData = {
          ...this.formData,
          ...data,
        }
      }

      const {
        delegacion = [],
        empresaCliente = [],
        tasadores = [],
        tipoEstadoInmueble = [],
        tipoFinalidadInmueble = [],
        tipoInmueble = [],
        tipoTrabajo = [],
        comerciales = [],
      } = related

      this.empresaClienteSelectOptions = empresaCliente.map(
        ({ id, clave }) => ({
          label: clave,
          value: id,
        }),
      )

      this.clientes = delegacion.map(({ id, nombre }) => ({
        label: nombre,
        value: id,
      }))

      this.propiertiesSelect = tipoInmueble.map(({ id, nombre }) => ({
        label: nombre,
        value: id,
      }))

      this.tasadoresRaw = tasadores
      this.tasadoresSelect = tasadores.map(({ clave }) => ({
        value: clave,
        label: clave,
      }))

      this.jobsSelect = tipoTrabajo.map(({ id, nombre }) => ({
        value: id,
        label: nombre,
      }))

      this.estadosInmueblesSelect = tipoEstadoInmueble.map(({ nombre }) => ({
        value: nombre,
        label: nombre,
      }))

      this.finalidadesInmuebleSelect = tipoFinalidadInmueble.map(
        ({ clave }) => ({
          value: clave,
          label: clave,
        }),
      )
      this.comercialesRaw = comerciales
      this.comercialesSelect = comerciales.map(({ clave }) => ({
        value: clave,
        label: clave,
      }))
    },

    async createNewCharge() {
      try {
        this.formErrors = {}
        const { data, message } = await chargeAPI.create(this.formData)

        this.$bfNotify({ title: 'Guardado', message, type: 'success' })

        return data
      } catch ({ errors, message }) {
        this.formErrors = errors
        this.$bfNotify({
          title: 'Error',
          message: 'Existe un error con tus datos',
          type: 'error',
        })

        return null
      }
    },

    async redirectToNewCharge(id) {
      this.$router.replace({
        name: 'charge',
        params: { id: `${id}` },
      })
    },

    async saveChargeClick() {
      const data = await this.createNewCharge()
      if (data) {
        this.redirectToNewCharge(data.id)
      }
    },

    async moveToBStatusWitchCheck(id) {
      try {
        const { message = '' } = await chargeAPI.moveToBWithValidation([id])
        this.$bfNotify({
          type: 'success',
          title: 'Encargos actualizados',
          message,
        })

        this.reloadPageData()
      } catch ({ message = '' }) {
        this.$bfNotify({
          type: 'error',
          title: 'No se ha podido mover a asignación de visitas',
          message:
            'Por favor, revisa que los campos obligatorios (con *) esten rellenos.',
        })
      }
    },
    async saveChargeAndMoveClick() {
      const data = await this.createNewCharge()

      if (data) {
        const { id } = data

        await this.moveToBStatusWitchCheck(id)

        this.redirectToNewCharge(id)
      }
    },

    async saveChargeAndCreateAnotherClick() {
      const data = await this.createNewCharge()

      if (data) {
        this.formData = {
          ...this.formData,
          numero_encargo_formateado: '',
          numero_encargo: '',
        }

        if (typeof window.scroll === 'function') {
          window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth',
          })
        } else {
          window.scrollTo(0, 0)
        }
      }
    },

    async changeSelectedBranch(newBranch) {
      await this.updateForm({
        key: 'sucursales_id',
        value: newBranch.id,
      })

      if (!this.isEditing) {
        this.updateForm({
          key: 'sucursal',
          value: newBranch,
        })
      } else {
        this.loadCharge()
      }
    },

    async createBranchModal(newData) {
      try {
        this.createBranchErrors = {}
        const { data, message } = await branchAPI.create(newData)

        await this.changeSelectedBranch(data)

        this.displayCreateBranchModal = false

        this.$bfNotify({ title: 'Guardado', message, type: 'success' })
      } catch ({ errors, message }) {
        this.createBranchErrors = errors
        this.$bfNotify({
          title: 'Error',
          message: 'Existe un error con tus datos',
          type: 'error',
        })
      }
    },

    async editBranchData(newData) {
      this.editBranchErrors = {}

      try {
        const { data, message = '' } = await branchAPI.update({
          id: newData.id,
          data: newData,
        })

        if (this.isEditing) {
          await this.loadCharge()
        } else {
          this.changeSelectedBranch(data)
        }

        this.$bfNotify({ title: 'Actualizado', message, type: 'success' })

        this.displayEditBranchModal = false
      } catch ({ errors }) {
        this.editBranchErrors = errors
        this.$bfNotify({
          title: 'Error',
          message: 'Existe un error con tus datos',
          type: 'error',
        })
      }
    },

    async changeSelectedApplicant(newApplicant) {
      await this.updateForm({
        key: 'solicitantes_id',
        value: newApplicant.id,
      })

      if (!this.isEditing) {
        this.updateForm({
          key: 'solicitante',
          value: newApplicant,
        })
      } else {
        this.loadCharge()
      }
    },

    async createApplicant(newData) {
      try {
        this.createApplicantErrors = {}
        const { data, message } = await clientAPI.create(newData)

        await this.changeSelectedApplicant(data)

        this.displayCreateApplicantModal = false

        this.$bfNotify({ title: 'Guardado', message, type: 'success' })
      } catch ({ errors, message }) {
        this.createApplicantErrors = errors
        this.$bfNotify({
          title: 'Error',
          message: 'Existe un error con tus datos',
          type: 'error',
        })
      }
    },

    async editApplicantData(newData) {
      this.editApplicantErrors = {}

      try {
        const { data, message } = await clientAPI.update({
          id: newData.id,
          data: newData,
        })

        if (this.isEditing) {
          await this.loadCharge()
        } else {
          this.changeSelectedApplicant(data)
        }

        this.displayEditApplicantModal = false

        this.$bfNotify({ title: 'Actualizado', message, type: 'success' })
      } catch ({ errors }) {
        this.editApplicantErrors = errors
        this.$bfNotify({
          title: 'Error',
          message: 'Existe un error con tus datos',
          type: 'error',
        })
      }
    },

    applicantHeaderButtonClick(type) {
      if (type === 'add') {
        this.displayCreateApplicantModal = true
      } else if (type === 'edit') {
        this.displayEditApplicantModal = true
      } else if (type === 'remove') {
        this.displayRemoveApplicantModal = true
      }
    },

    branchHeaderButtonClick(type) {
      if (type === 'add') {
        this.displayCreateBranchModal = true
      } else if (type === 'edit') {
        this.displayEditBranchModal = true
      } else if (type === 'remove') {
        this.displayRemoveBranchModal = true
      }
    },

    selectApplicant(data) {
      this.changeSelectedApplicant(data)
    },

    selectBranch(data) {
      this.changeSelectedBranch(data)
    },

    async updateCodigoPostal({ value }) {
      const valueLength = value.length
      if (valueLength >= 5) {
        try {
          const datosCp = await chargeAPI.autoCodigosPostales(value)
          this.formData.inmueble_provincia = datosCp.provincia
          this.formData.inmueble_poblacion = datosCp.poblacion
          this.formData.inmueble_codigopostal = value
          console.log('now updating to back')
          await chargeAPI.update({
            id: this.id,
            data: this.formData,
          })
        } catch ({ errors, message }) {
          this.formErrors = errors
          this.$bfNotify({
            title: 'Error',
            message,
            type: 'error',
          })
        }
      }
    },

    async updateForm({ key, value }) {
      if (key === 'urgente' && !this.isAdmin) {
        /* eslint-disable-next-line no-alert */
        alert('No tiene permiso para modificar este campo.')
        return
      }

      const newValue =
        key !== 'numero_encargo' ? value : value.replace(/[.]/g, '')

      const newData = { [key]: newValue }

      if (!this.isEditing) {
        this.formData = { ...this.formData, ...newData }
      } else {
        this.formErrors = {}

        try {
          const { data, message } = await chargeAPI.update({
            id: this.id,
            data: newData,
          })

          this.formData = data
          this.$bfNotify({ title: 'Actualizado', message, type: 'success' })
        } catch ({ errors }) {
          this.formErrors = errors
          this.$bfNotify({
            title: 'Error',
            message: 'Existe un error con tus datos',
            type: 'error',
          })
        }
      }
    },

    updateEstado({ value }) {
      this.updateForm({
        key: 'estado',
        value,
      })
    },

    updateTasadorOficial({ key, value }) {
      const selectedTasador = this.tasadoresRaw.find(
        ({ clave }) => clave === value,
      )
      if (selectedTasador) {
        this.updateForm({
          key,
          value: selectedTasador.id,
        })
      }
    },
    updateComercial({ key, value }) {
      const selctedComercial = this.comercialesRaw.find(
        ({ clave }) => clave === value,
      )
      if (selctedComercial) {
        this.updateForm({
          key,
          value: selctedComercial.id,
        })
      }
    },
    async removeBranch() {
      await this.updateForm({
        key: 'sucursales_id',
        value: '',
      })

      if (!this.isEditing) {
        this.updateForm({
          key: 'sucursal',
          value: {},
        })
      }

      this.displayRemoveBranchModal = false
    },

    async removeApplicant() {
      await this.updateForm({
        key: 'solicitantes_id',
        value: '',
      })

      if (!this.isEditing) {
        this.updateForm({
          key: 'solicitante',
          value: {},
        })
      }

      this.displayRemoveApplicantModal = false
    },

    changeDocumentationFilterCheckbox(id) {
      const index = this.documentationActiveFilters.indexOf(id)

      if (index >= 0) {
        this.$delete(this.documentationActiveFilters, index)
      } else {
        this.documentationActiveFilters = [
          ...this.documentationActiveFilters,
          id,
        ]
      }

      this.documentationCurrentPage = 1
      this.loadDocuments()
    },

    /* eslint-disable-next-line func-names */
    updateDocumentationSearch: debounce(async function () {
      this.documentationCurrentPage = 1
      this.loadDocuments()
    }, 350),

    documentationTableHeaderCellClassName({ columnIndex }) {
      return columnIndex > 0 ? 'text-center' : ''
    },

    documentationTableCellClassName({ columnIndex }) {
      return columnIndex > 0
        ? 'text-center tasaciones-small-cell small-paco'
        : 'tasaciones-small-cell'
    },

    documentationTableRowClick({ column: { property = '' }, row }) {
      this.clearUploadFiles()

      switch (property) {
        case 'titulo':
          this.editingExistingDocument = true
          this.displayDocumentationFormModal = true
          this.selectedDocumentationTableRow = row
          break
        case 'open':
          window.open(`${backendUrlBase}${row.documento}`, 'download')
          // window.location.assign(`${backendUrlBase}${row.documento}`)

          break
        case 'delete':
          this.displayDeleteDocumentationModal = true
          this.selectedDocumentationTableRow = row
          break
        default:
          break
      }
    },

    async deleteDocumentation() {
      const { id } = this.selectedDocumentationTableRow
      const { message } = await documentAPI.delete(id)
      await this.loadDocuments()

      this.$bfNotify({
        type: 'success',
        title: message,
        message: 'Documento eliminado con éxito.',
      })

      this.displayDeleteDocumentationModal = false
      this.selectedDocumentationTableRow = {}
    },

    async saveDocumentationFileForm({ titulo, categoria }) {
      if (!this.editingExistingDocument) {
        try {
          this.documentationIsUploading = true

          const { notificationPayload } = await uploadChargeDocuments({
            files: this.documentationUploadFiles,
            title: titulo,
            category: categoria,
            chargeId: this.id,
            countProgressCallback: (p) => {
              this.documentationCountProgress = p
            },
            sizeProgressCallback: (p) => {
              this.documentationSizeProgress = p
            },
          })

          this.$bfNotify(notificationPayload)

          await this.loadDocuments()

          this.displayDocumentationFormModal = false
          this.clearUploadFiles()
        } catch ({ errors }) {
          this.documentationFormErrors = errors
        } finally {
          this.documentationIsUploading = false
        }
      } else {
        const { id } = this.selectedDocumentationTableRow
        const { message } = await documentAPI.update({
          id,
          data: {
            titulo,
            categoria,
          },
        })

        await this.loadDocuments()

        this.$bfNotify({
          type: 'success',
          title: message,
          message: 'Documento editado con éxito.',
        })

        this.displayDocumentationFormModal = false
      }
    },

    documentationFilesAdded(files) {
      this.editingExistingDocument = false
      this.documentationUploadFiles = files
      this.displayDocumentationFormModal = true
    },

    clearUploadFiles() {
      this.documentationUploadFiles = []
      this.documentationFormErrors = {}
      this.documentationSizeProgress = {}
      this.documentationCountProgress = {}
      this.$refs.documentDropzone.$refs.fileDropzone.removeAllFiles()
    },

    headerWarningButtonClick() {
      this.displayWarningsFormModal = true
    },

    async updateChargeWarningModal(payload) {
      await this.updateForm(payload)
      this.displayWarningsFormModal = false
    },

    closeWarningNotice() {
      this.displayWarningNotice = false
    },

    deleteChargeButtonClick() {
      this.displayDeleteChargeModal = true
    },
    async deleteCharge() {
      try {
        const { message = '' } = await chargeAPI.delete(this.id)
        this.$bfNotify({
          type: 'success',
          title: 'Encargo eliminado',
          message,
        })

        this.$router.replace({ name: 'charges' })
      } catch ({ errors = '' }) {
        this.$bfNotify({
          type: 'error',
          title: 'Error',
          message: errors,
        })
      } finally {
        this.displayDeleteChargeModal = false
      }
    },

    updateDocumentationPagination(newPage) {
      this.documentationCurrentPage = newPage
      this.loadDocuments()
    },
  },

  computed: {
    allTableRowsSelected() {
      return this.documentationTableData.reduce(
        (acc, documentation) =>
          acc
            ? this.checkedDocumentationRows.includes(documentation.id)
            : false,
        true,
      )
    },
    changeDocumentationTitleButton() {
      if (this.checkedDocumentationRows.length > 0) {
        return true
      }
    },
    ...mapState({
      user: ({ auth }) => auth.user,
    }),
    ...mapGetters('auth', ['isAdmin']),
    ...mapGetters('auth', ['isTasador', 'roles']),
    ...mapGetters('auth', ['isColaborador', 'roles']),

    applicantFormData() {
      const applicant = get(this.formData, ['solicitante'], null)

      return applicant !== null ? applicant : undefined
    },

    branchFormData() {
      const branch = get(this.formData, ['sucursal'], null)

      return branch !== null ? branch : undefined
    },

    displayDeleteChargeButton() {
      return this.isEditing && this.isAdmin
    },

    documentationFilterCheckboxes() {
      return this.documentationCategories.map((label, index) => ({
        label,
        id: index,
        active: this.documentationActiveFilters.indexOf(index) >= 0,
      }))
    },

    documentationFormData() {
      if (this.editingExistingDocument) {
        return this.selectedDocumentationTableRow
      }

      const files = this.documentationUploadFiles
      return {
        titulo: files.length === 1 ? files[0].name : '',
      }
    },

    documentationTableColumns() {
      return [
        {
          label: 'Documento',
          dataKey: 'titulo',
        },
        {
          label: 'Categoría',
          dataKey: 'categoriaNombre',
          width: 120,
        },
        {
          label: 'Abrir',
          dataKey: 'open',
          width: 120,
        },
        {
          label: 'Eliminar',
          dataKey: 'delete',
          width: 120,
        },
      ]
    },

    documentationTableData() {
      if (!this.documentationData.length) {
        return []
      }

      return this.documentationData.map(
        ({
          titulo,
          id,
          documento,
          categoria,
          categoria_nombre: categoriaNombre,
        }) => ({
          titulo,
          id,
          documento,
          categoria,
          categoriaNombre,
        }),
      )
    },

    hasSelectedApplicant() {
      return (
        !!get(this.formData, ['solicitantes_id'], '') &&
        !isEmpty(this.applicantFormData)
      )
    },

    hasSelectedBranch() {
      return (
        !!get(this.formData, ['sucursales_id'], '') &&
        !isEmpty(this.branchFormData)
      )
    },

    isEditing() {
      return !!this.id
    },

    missingApplicantError() {
      const applicantError = get(this.formErrors, ['solicitante'], [])
      const applicantIdError = get(this.formErrors, ['solicitantes_id'], [])

      return [...applicantError, ...applicantIdError].filter((error) => !!error)
    },

    multipleUploadFiles() {
      return this.documentationUploadFiles.length > 1
    },

    pageTitle() {
      return this.isEditing
        ? `Encargo: ${this.formData.numero_encargo || ''} | COD: ${
            this.formData.codigo || ''
          } `
        : 'Crear Encargo'
    },

    selectedTasadorOficial() {
      return get(this.formData, ['tasador_oficial', 'clave'], '')
    },
    selectedComercial() {
      return get(this.formData, ['comercial', 'clave'], '')
    },
    puedeSerAnulado() {
      if (this.isAdmin || this.isColaborador) {
        return true
      }
      return false
    },
  },

  components: {
    ApplicantForm,
    SolicitanteEnEncargoForm,
    BranchForm,
    ChargeWarningModal,
    CreateApplicantModal,
    CreateCodigoPostalModal,
    CreateBranchModal,
    DeleteWarningModal,
    EditApplicantModal,
    EditBranchModal,
    documentacionChangeNameModal,
    documentacionChangeCategoryModal,
    FileDropzone,
    FileFormModal,
    FormSectionHeader,
    LoadingIndicator,
    PageWrap,
  },
}
</script>

<style lang="scss" scoped>
.small-paco {
  padding: 0px 0 !important;
}
.mb0 {
  margin-bottom: 0px !important;
}
.charge-detail-wrap {
  &.-warning-notice-visible {
    position: relative;

    > *:not(.charge-warning-overlay) {
      opacity: 0.35;
      pointer-events: none;
    }

    > .charge-warning-overlay {
      opacity: 1;
    }
  }
}

.charge-warning-overlay {
  position: absolute;
  left: 50%;
  top: 10%;
  transform: translateX(-50%);
  width: 75%;
  max-width: 800px;
  padding: 20px 30px;
  opacity: 0;
  border-radius: 5px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);

  .warning-overlay-header {
    display: flex;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    margin-bottom: 10px;

    .header-icon {
      margin-right: 10px;
    }

    .header-label {
      font-size: 18px;
      font-weight: bold;
      margin: 0;
    }

    .header-close-button {
      appearance: none;
      background: none;
      border: none;
      outline: none;
      margin: 0;
      margin-left: auto;
      cursor: pointer;
      font-size: 20px;
    }
  }

  .warning-body {
    margin: 0;
    font-size: 26px;
  }
}

.documentation-filter-checkbox {
  /deep/ .bf-label-wrap {
    display: none;
  }
}

.documentation-table-icon {
  font-size: 20px;
}
</style>
